import React from "react";
import { formatLocationAddress } from "../adminUtils";

const formatBlockDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminInventoryTab = ({
  t,
  isActive,
  dashboardLoading,
  dashboardError,
  equipmentSnapshot,
  selectedEquipmentTypeId,
  setSelectedEquipmentTypeId,
  selectedEquipment,
  locationsLoading,
  locationsError,
  locations,
  equipmentAdjustments,
  equipmentBlockForms,
  handleEquipmentAdjustmentChange,
  handleEquipmentAvailableChange,
  handleEquipmentSave,
  handleEquipmentBlockFormChange,
  handleEquipmentBlockCreate,
  handleEquipmentBlockDelete,
  equipmentSavingId,
  equipmentBlockActionId,
}) => (
<div
              className={`admin-tab ${isActive ? "admin-tab--active" : ""}`.trim()}
            >
              <section className="admin-section admin-animate admin-animate--delay-6">
                <header>
                  <div>
                    <p className="admin-section__eyebrow">
                      {t("admin.inventory.equipmentEyebrow", "Equipment dashboard")}
                    </p>
                    <h2>{t("admin.inventory.equipmentHeading", "Equipment status overview")}</h2>
                  </div>
                </header>
                {dashboardLoading ? (
                  <p className="admin-empty">
                    {t("admin.inventory.loading", "Loading equipment overview...")}
                  </p>
                ) : dashboardError ? (
                  <p className="admin-empty">{dashboardError}</p>
                ) : equipmentSnapshot.length === 0 ? (
                  <p className="admin-empty">
                    {t("admin.inventory.empty", "No equipment types configured yet.")}
                  </p>
                ) : (
                  <ul className="admin-list admin-list--compact admin-equipment__list">
                    {equipmentSnapshot.map((item) => (
                      <li
                        key={item.id}
                        className={`admin-item admin-equipment__item ${
                          selectedEquipmentTypeId === item.id ? "admin-item--selected" : ""
                        }`.trim()}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedEquipmentTypeId === item.id}
                        onClick={() =>
                          setSelectedEquipmentTypeId((prev) => (prev === item.id ? null : item.id))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedEquipmentTypeId((prev) =>
                              prev === item.id ? null : item.id
                            );
                          }
                        }}
                      >
	                        <div className="admin-equipment__header">
	                          <div>
	                            <h3>{item.name}</h3>
	                          </div>
	                        </div>
	                        <div className="admin-equipment__stats">
                          <div className="admin-equipment__stat">
                            <span className="admin-equipment__label">
                              {t("admin.inventory.total", "Total units")}
                            </span>
                            <span className="admin-equipment__value">{item.total}</span>
                          </div>
	                          <div className="admin-equipment__stat admin-equipment__stat--available">
	                            <span className="admin-equipment__label">
	                              {t("admin.inventory.available", "Available units")}
	                            </span>
	                            <span className="admin-equipment__value">{item.available}</span>
	                          </div>
	                          <div className="admin-equipment__stat admin-equipment__stat--maintenance">
	                            <span className="admin-equipment__label">
	                              {t("admin.inventory.maintenance", "Under maintenance")}
	                            </span>
                            <span className="admin-equipment__value">{item.maintenance}</span>
                          </div>
                          <div className="admin-equipment__stat admin-equipment__stat--lost">
                            <span className="admin-equipment__label">
                              {t("admin.inventory.lost", "Lost / disabled")}
                            </span>
                            <span className="admin-equipment__value">{item.lost}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="admin-equipment__editor">
                  <div className="admin-equipment__editor-head">
                    <div>
                      <p className="admin-section__eyebrow">
                        {t("admin.inventory.locationCounts", "Location counts")}
                      </p>
                    </div>
                    
                  </div>
                  {!selectedEquipment ? (
                    <p className="admin-empty">
                      {t(
                        "admin.inventory.selectTypePrompt",
                        "Select an equipment type above to edit location counts."
                      )}
                    </p>
                  ) : locationsLoading ? (
                    <p className="admin-empty">
                      {t("admin.locations.loading", "Loading locations...")}
                    </p>
                  ) : locationsError ? (
                    <p className="admin-empty">{locationsError}</p>
                  ) : locations.length === 0 ? (
                    <p className="admin-empty">
                      {t("admin.locations.empty", "No locations configured yet.")}
                    </p>
                  ) : (
                    <div className="admin-equipment__locations">
		                      {locations.map((location) => {
		                        const values = equipmentAdjustments[location.id] || {
		                          quantity: 0,
		                          maintenance: 0,
		                          lost: 0,
		                        };
                          const quantity = Number.parseInt(values.quantity, 10) || 0;
                          const maintenance = Number.parseInt(values.maintenance, 10) || 0;
                          const lost = Number.parseInt(values.lost, 10) || 0;
                          const available = Math.max(0, quantity - maintenance - lost);
                          const inventoryItem = location.inventory.find(
                            (entry) => entry.equipmentTypeId === selectedEquipmentTypeId
                          );
                          const blocks = Array.isArray(inventoryItem?.blocks)
                            ? inventoryItem.blocks
                            : [];
                          const blockForm = equipmentBlockForms[location.id] || {
                            quantity: "1",
                            startLocal: "",
                            endLocal: "",
                            reason: "",
                          };
	                        return (
	                          <div key={location.id} className="admin-equipment__location">
	                            <div className="admin-equipment__location-info">
	                              <h4>{location.name}</h4>
	                              <p className="admin-item__note">
	                                {formatLocationAddress(location)}
	                              </p>
	                            </div>
                              <div className="admin-equipment__location-counts">
		                            <div className="admin-equipment__inputs">
	                              <div>
	                                <label className="admin-form__label" htmlFor={`qty-${location.id}`}>
	                                  {t("admin.inventory.total", "Total units")}
	                                </label>
	                                <input
	                                  id={`qty-${location.id}`}
	                                  type="number"
	                                  min="0"
	                                  className="admin-input admin-input--compact"
	                                  value={values.quantity}
	                                  onChange={handleEquipmentAdjustmentChange(location.id, "quantity")}
	                                />
	                              </div>
                              <div>
                                <label
                                  className="admin-form__label"
                                  htmlFor={`avail-${location.id}`}
                                >
                                  {t("admin.inventory.available", "Available units")}
                                </label>
                                <input
                                  id={`avail-${location.id}`}
                                  type="number"
                                  min="0"
                                  className="admin-input admin-input--compact"
                                  value={available}
                                  onChange={handleEquipmentAvailableChange(location.id)}
                                />
                              </div>
		                              <div>
		                                <label
		                                  className="admin-form__label"
		                                  htmlFor={`maint-${location.id}`}
	                                >
                                  {t("admin.inventory.maintenance", "Under maintenance")}
                                </label>
                                <input
                                  id={`maint-${location.id}`}
                                  type="number"
                                  min="0"
                                  className="admin-input admin-input--compact"
                                  value={values.maintenance}
                                  onChange={handleEquipmentAdjustmentChange(
                                    location.id,
                                    "maintenance"
                                  )}
                                />
                              </div>
                              <div>
                                <label
                                  className="admin-form__label"
                                  htmlFor={`lost-${location.id}`}
                                >
                                  {t("admin.inventory.lost", "Lost / disabled")}
                                </label>
                                <input
                                  id={`lost-${location.id}`}
                                  type="number"
                                  min="0"
                                  className="admin-input admin-input--compact"
                                  value={values.lost}
                                  onChange={handleEquipmentAdjustmentChange(location.id, "lost")}
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              className="admin-button admin-button--outline"
                              onClick={() => handleEquipmentSave(location.id)}
                              disabled={equipmentSavingId === location.id}
                            >
                              {equipmentSavingId === location.id
                                ? t("admin.inventory.saving", "Saving...")
                                : t("admin.inventory.save", "Save")}
                            </button>
                              </div>
                              <div className="admin-equipment-blocks">
                                <div className="admin-equipment-blocks__head">
                                  <div>
                                    <span className="admin-form__label">
                                      {t("admin.inventory.blocks", "Timed blocks")}
                                    </span>
                                  </div>
                                </div>
                                <div className="admin-equipment-blocks__form">
                                  <label>
                                    <span className="admin-form__label">
                                      {t("admin.inventory.blockQuantity", "Qty")}
                                    </span>
                                    <input
                                      type="number"
                                      min="1"
                                      className="admin-input admin-input--compact"
                                      value={blockForm.quantity}
                                      onChange={handleEquipmentBlockFormChange(
                                        location.id,
                                        "quantity"
                                      )}
                                    />
                                  </label>
                                  <label>
                                    <span className="admin-form__label">
                                      {t("admin.inventory.blockFrom", "From")}
                                    </span>
                                    <input
                                      type="datetime-local"
                                      className="admin-input"
                                      value={blockForm.startLocal}
                                      onChange={handleEquipmentBlockFormChange(
                                        location.id,
                                        "startLocal"
                                      )}
                                    />
                                  </label>
                                  <label>
                                    <span className="admin-form__label">
                                      {t("admin.inventory.blockUntil", "Until")}
                                    </span>
                                    <input
                                      type="datetime-local"
                                      className="admin-input"
                                      value={blockForm.endLocal}
                                      onChange={handleEquipmentBlockFormChange(
                                        location.id,
                                        "endLocal"
                                      )}
                                    />
                                  </label>
                                  <label>
                                    <span className="admin-form__label">
                                      {t("admin.inventory.blockReason", "Note")}
                                    </span>
                                    <input
                                      type="text"
                                      className="admin-input"
                                      maxLength={200}
                                      value={blockForm.reason}
                                      onChange={handleEquipmentBlockFormChange(
                                        location.id,
                                        "reason"
                                      )}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="admin-button admin-button--primary"
                                    onClick={() => handleEquipmentBlockCreate(location.id)}
                                    disabled={equipmentBlockActionId === `${location.id}:create`}
                                  >
                                    {equipmentBlockActionId === `${location.id}:create`
                                      ? t("admin.inventory.blocking", "Blocking...")
                                      : t("admin.inventory.blockEquipment", "Block")}
                                  </button>
                                </div>
                                <div className="admin-equipment-blocks__list">
                                  {blocks.length === 0 ? (
                                    <p className="admin-item__note">
                                      {t(
                                        "admin.inventory.noBlocks",
                                        "No timed blocks for this equipment."
                                      )}
                                    </p>
                                  ) : (
                                    blocks.map((block) => (
                                      <div
                                        className="admin-equipment-blocks__item"
                                        key={block.id}
                                      >
                                        <span>
                                          <strong>{block.quantity}</strong>{" "}
                                          {formatBlockDateTime(block.startUtc)}-
                                          {formatBlockDateTime(block.endUtc)}
                                          {block.reason ? ` · ${block.reason}` : ""}
                                        </span>
                                        <button
                                          type="button"
                                          className="admin-button admin-button--danger-outline"
                                          onClick={() =>
                                            handleEquipmentBlockDelete(location.id, block.id)
                                          }
                                          disabled={equipmentBlockActionId === block.id}
                                        >
                                          {equipmentBlockActionId === block.id
                                            ? t("admin.inventory.removing", "Removing...")
                                            : t("admin.inventory.removeBlock", "Remove")}
                                        </button>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>

              <section className="admin-grid admin-animate admin-animate--delay-7">
                <article className="admin-panel">
                  <header>
                    <h2>{t("admin.inventory.heading", "Inventory snapshot")}</h2>
                    <p>
                      {t(
                        "admin.inventory.description",
                        "Live inventory counts by location (read-only)."
                      )}
                    </p>
                  </header>
                  <ul className="admin-list">
                    {locationsLoading ? (
                      <li className="admin-empty">
                        {t("admin.locations.loading", "Loading locations...")}
                      </li>
                    ) : locationsError ? (
                      <li className="admin-empty">{locationsError}</li>
                    ) : locations.length === 0 ? (
                      <li className="admin-empty">
                        {t("admin.locations.empty", "No locations configured yet.")}
                      </li>
                    ) : (
	                    locations.map((location) => {
	                      const inventoryTags = location.inventory.map((item) => {
	                        const parts = [`${item.equipmentTypeName} · ${item.quantity}`];
	                        if (item.maintenanceQuantity) parts.push(`Maint ${item.maintenanceQuantity}`);
	                        if (item.lostQuantity) parts.push(`Lost ${item.lostQuantity}`);
	                        return parts.join(" · ");
	                      });
                      return (
                        <li key={location.id} className="admin-item">
                            <div className="admin-item__header">
                              <div>
                                <h3>{location.name}</h3>
                                <p className="admin-item__meta">
                                  {formatLocationAddress(location)}
                                </p>
                              </div>
                              <span
                                className={`admin-pill admin-pill--${
                                  location.isActive ? "success" : "muted"
                                }`}
                              >
                                {location.isActive
                                  ? t("admin.locations.active", "Active")
                                  : t("admin.locations.inactive", "Inactive")}
                              </span>
                            </div>
                            <div className="admin-tags">
                              {inventoryTags.length === 0 ? (
                                <span className="admin-tag">
                                  {t("admin.locations.noInventory", "No inventory")}
                                </span>
                              ) : (
                                inventoryTags.map((tag) => (
                                  <span key={tag} className="admin-tag">
                                    {tag}
                                  </span>
                                ))
                              )}
                            </div>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </article>
              </section>
            </div>
            
);

export default AdminInventoryTab;
